import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Pencil } from 'lucide-react';
import { createGeneralSettings, getGeneralSettings, updateGeneralSettings } from 'services/settings.service';
import Loading from 'Common/Components/Loading';

enum SettingsKey {
  GoogleReviewLink = 'GoogleReviewLink',
  JobNimbusCrmLink = 'JobNimbusCrmLink',
}

const General: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [ggReviewSection, setGgReviewSection] = useState<any>(null);
  const [jobNimbusSection, setJobNimbusSection] = useState<any>(null);
  const [isEditingGgReview, setIsEditingGgReview] = useState(false);
  const [isEditingJobNimbus, setIsEditingJobNimbus] = useState(false);
  const [generalSettings, setGeneralSettings] = useState<any[]>([]);
  const [ggReviewValue, setGgReviewValue] = useState<any>(null);
  const [jobNimbusValue, setJobNimbusValue] = useState<any>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [validationError, setValidationError] = useState<string>('');

  useEffect(() => {
    loadSettings();
  }, [])

  const loadSettings = async () => {
    setIsLoading(true);
    try {
      const settingsResponse = await getGeneralSettings();
      setGgReviewSection(settingsResponse.find((s: any) => s.key === SettingsKey.GoogleReviewLink));
      setJobNimbusSection(settingsResponse.find((s: any) => s.key === SettingsKey.JobNimbusCrmLink));
      setGgReviewValue(settingsResponse.find((s: any) => s.key === SettingsKey.GoogleReviewLink)?.value);
      setJobNimbusValue(settingsResponse.find((s: any) => s.key === SettingsKey.JobNimbusCrmLink)?.value);
    } catch (error) {
      // toast.error('Failed to load settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveGgReview = async () => {
    // Validation: Check URL format
    const urlPattern = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
    if (!urlPattern.test(ggReviewValue) && ggReviewValue.trim() !== '') {
      setValidationError('Please enter a valid URL format');
      // toast.error('Please enter a valid URL format', { autoClose: 2000 });
      return;
    }

    // Clear validation error if validation passes
    setValidationError('');

    try {
      setIsSaving(true);
      await new Promise((resolve) => setTimeout(resolve, 1000));

      const payload = {
        key: SettingsKey.GoogleReviewLink,
        value: ggReviewValue,
        description: 'Google Review Link',
        isActive: true
      }

      if (payload && !!ggReviewSection?.id) {
        const response = await updateGeneralSettings(ggReviewSection?.id, payload);
        setGgReviewSection(response)
        setGgReviewValue(response?.value);
        toast.success('Link updated successfully', { autoClose: 2000 });
      } else {
        const response = await createGeneralSettings(payload);
        setGgReviewSection(response);
        setGgReviewValue(response?.value);
        toast.success('Link created successfully', { autoClose: 2000 });
      }
    } catch (error) {
      toast.error('Failed to save link');
    } finally {
      setIsSaving(false);
      setIsEditingGgReview(false);
    }
  };

  const handleSaveJobNimbus = async () => {
    try {
      const payload = {
        key: SettingsKey.JobNimbusCrmLink,
        value: jobNimbusValue,
        description: 'Job Nimbus CRM Link',
        isActive: true
      }

      if (payload && jobNimbusSection.id) {
        const response = await updateGeneralSettings(jobNimbusSection.id, payload);
        setJobNimbusSection(response)
        setJobNimbusValue(response?.value);
        setIsEditingJobNimbus(false);
      }
      setIsEditingJobNimbus(false);
    } catch (error) {
      toast.error('Failed to save link');
    } finally {
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <Loading size="sm" color="blue" isVisible={true} />
      </div>
    )
  }

  return (
    <div className="w-full max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">General Settings</h2>
          <p className="text-sm text-gray-600 mt-1">Configure your general application preferences</p>
        </div>
        <div className="p-6">
          {/* Google Review Link */}
          <div className="mt-6">
            <label htmlFor="ggReviewLink" className="block text-sm font-medium text-gray-700 mb-2">
              Google Review Link
            </label>
            <div className="flex space-x-2 items-start">
              <div className="flex-1">
                <input
                  type="url"
                  value={isEditingGgReview ? ggReviewValue : ggReviewSection?.value}
                  onChange={(e) => {
                    setGgReviewValue(e.target.value);
                    setValidationError(''); // Clear error on change
                  }}
                  readOnly={!isEditingGgReview}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none ${validationError && isEditingGgReview
                    ? 'border-red-500 focus:ring-2 focus:ring-red-500'
                    : 'border-gray-300'
                    } ${isEditingGgReview ? 'focus:ring-2 focus:ring-blue-500 focus:border-blue-500' : ''}`}
                  placeholder="Enter Google Review link"
                />
                {validationError && isEditingGgReview && (
                  <p className="mt-1 text-sm text-red-600">{validationError}</p>
                )}
              </div>
              <button
                onClick={isEditingGgReview ? handleSaveGgReview : () => setIsEditingGgReview(true)}
                disabled={isLoading || isSaving}
                className={`min-w-[80px] h-[42px] px-4 py-2 rounded-md focus:outline-none flex-shrink-0 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed ${isEditingGgReview ? 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500' : 'bg-white-600 text-black hover:bg-gray-100 focus:ring-gray-500'}`}
              >
                {isSaving ? (
                  <Loading size="xs" color="blue" isVisible={true} />
                ) : isEditingGgReview ? 'Save' : <Pencil className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {/* Job Nimbus CRM Link */}
          {/* <div className="mt-6">
            <label htmlFor="jobNimbusLink" className="block text-sm font-medium text-gray-700 mb-2">
              Job Nimbus CRM Link
            </label>
            <div className="flex space-x-2">
              <input  
                type="url"
                value={isEditingJobNimbus ? jobNimbusLinkValue : jobNimbusLink?.value || 'https://forms.gle/y2Ka41z3mcm6BSXK6'}
                onChange={(e) => setJobNimbusLinkValue(e.target.value)}
                readOnly={!isEditingJobNimbus}
                className={`flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none ${isEditingJobNimbus ? 'focus:ring-2 focus:ring-blue-500 focus:border-blue-500' : ''}`}
                placeholder="Enter Job Nimbus CRM link"
              />
              <button
                onClick={isEditingJobNimbus ? handleSaveJobNimbus : () => setIsEditingJobNimbus(true)}
                disabled={isLoading}
                className={`px-4 py-2 rounded-md focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed ${isEditingJobNimbus ? 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500' : 'bg-white-600 text-black hover:bg-gray-100 focus:ring-gray-500'}`}
              >
                {isLoading ? 'Saving...' : isEditingJobNimbus ? 'Save' : <Pencil className="w-4 h-4" />}
              </button>
            </div>
          </div> */}
        </div>
      </div>
    </div>
  );
};

export default General;